import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import type { CreditOption } from '@/api/extensions/useRegistrationOps';

interface CreditApplyControlProps {
  /** Per-org credit affordances for this basket (each already capped to that
   *  org's portion of the basket). Always length ≥ 2 here — the single-org
   *  case uses the simpler inline control in StripeCheckoutDialog. */
  options: CreditOption[];
  /** Currently-applied amount per org (org id → dollars). */
  appliedByOrg: Record<string, number>;
  /** Emits the next applied-by-org map whenever the user applies/removes. */
  onChange: (next: Record<string, number>) => void;
}

const round = (n: number) => Math.round(n * 100) / 100;

/**
 * Multi-org credit applicator for the bulk "Pay outstanding balance" flow.
 *
 * Org account credit is org-scoped, so when a basket spans several orgs the
 * user picks which org's credit to spend and how much — each capped to that
 * org's portion of the basket. Applying credit drops a per-org badge; changing
 * an org's amount updates its existing badge rather than adding a second.
 */
export function CreditApplyControl({
  options,
  appliedByOrg,
  onChange,
}: CreditApplyControlProps) {
  const [selectedOrgId, setSelectedOrgId] = useState(
    options[0]?.organization_id ?? '',
  );
  const [input, setInput] = useState('');

  const selected = useMemo(
    () => options.find((o) => o.organization_id === selectedOrgId) ?? options[0],
    [options, selectedOrgId],
  );

  // Pre-fill the input with this org's currently-applied amount when the
  // selection changes, so re-selecting an org shows what's already on it (and
  // the user can adjust it up or down).
  useEffect(() => {
    const applied = selected ? appliedByOrg[selected.organization_id] ?? 0 : 0;
    setInput(applied > 0 ? String(applied) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  if (!selected) return null;

  const orgName = (o: CreditOption) =>
    o.organization_name?.trim() || 'Organization';

  const cap = selected.applicable_credit;
  const apply = () => {
    const parsed = parseFloat(input);
    const amount = isNaN(parsed) || parsed < 0 ? 0 : round(Math.min(parsed, cap));
    const next = { ...appliedByOrg };
    if (amount > 0) next[selected.organization_id] = amount;
    else delete next[selected.organization_id];
    onChange(next);
  };

  const removeOrg = (orgId: string) => {
    const next = { ...appliedByOrg };
    delete next[orgId];
    onChange(next);
    if (orgId === selectedOrgId) setInput('');
  };

  const appliedEntries = options
    .map((o) => ({ option: o, amount: appliedByOrg[o.organization_id] ?? 0 }))
    .filter((e) => e.amount > 0);

  return (
    <div className="mt-3 pt-3 border-t space-y-2">
      <p className="text-xs font-medium">Apply account credit</p>

      {/* Org picker — credit is org-scoped, so the user chooses whose credit
          to spend on their share of this basket. */}
      <select
        value={selectedOrgId}
        onChange={(e) => setSelectedOrgId(e.target.value)}
        className="w-full h-8 rounded-md border px-2 text-sm bg-background"
        aria-label="Choose an organization to apply credit from"
      >
        {options.map((o) => (
          <option key={o.organization_id} value={o.organization_id}>
            {orgName(o)}
          </option>
        ))}
      </select>

      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          Total credit
          <span className="block text-foreground font-medium">
            {formatCurrency(selected.total_credit, 2)}
          </span>
        </span>
        <span className="text-right">
          Available to use here
          <span className="block text-foreground font-medium">
            {formatCurrency(selected.applicable_credit, 2)}
          </span>
        </span>
      </div>

      <div className="flex items-center gap-1">
        <input
          type="number"
          min="0"
          step="0.01"
          max={cap}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="w-24 h-8 rounded-md border px-2 text-sm text-right bg-background"
          placeholder="0.00"
          aria-label={`Credit amount to apply from ${orgName(selected)}`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 px-2 text-xs"
          onClick={() => setInput(String(cap))}
        >
          Max
        </Button>
        <Button
          type="button"
          size="sm"
          className="h-8 px-3 text-xs"
          onClick={apply}
        >
          Apply
        </Button>
      </div>

      {appliedEntries.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {appliedEntries.map(({ option, amount }) => (
            <span
              key={option.organization_id}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 px-2 py-0.5 text-[11px] font-medium"
            >
              {orgName(option)} · {formatCurrency(amount, 2)}
              <button
                type="button"
                onClick={() => removeOrg(option.organization_id)}
                className="hover:opacity-70"
                aria-label={`Remove credit from ${orgName(option)}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
